import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useMemo, useState } from "react";
import AnimateHeight from "react-animate-height";
import styles from "./InterventionWidget.module.scss";
import { LinkType, Links } from "../Links/Links";
import { Spinner } from "../../../shared/components/Spinner/Spinner";
import { thousandize } from "../../../../util/formatting";
import { PortableText } from "@portabletext/react";
import { NavLink } from "../../../shared/components/Navbar/Navbar";
import { stegaClean } from "@sanity/client/stega";
import { EffektButton } from "../../../shared/components/EffektButton/EffektButton";
import { WidgetContext } from "../../layout/layout";
import { usePlausible } from "next-plausible";
export type InterventionWidgetOutputConfiguration = {
  interventions: SanityIntervention[];
  explanation_label: string;
  explanation_text: any[];
  explanation_links?: (LinkType | NavLink)[];
  donate_button?: boolean;
  donate_label_short?: string;
  locale: string;
};
export type SanityIntervention = {
  title: string;
  organization_name: string;
  abbreviation: string;
  template_string: string;
  organization_id: number;
  cause_area_id: number;
};
type Intervention = {
  title: string;
  organizationName: string;
  abbreviation: string;
  pricePerOutput?: number;
  outputStringTemplate: string;
  organizationId: number;
  causeAreaId: number;
  shortDescription: string;
};
export const InterventionWidgetOutput: React.FC<{
  sum: number;
  configuration: InterventionWidgetOutputConfiguration;
  currency: string;
  locale: string;
}> = t0 => {
  const $ = _c(51);
  const {
    sum,
    configuration,
    currency
  } = t0;
  const {
    interventions,
    explanation_label,
    explanation_text,
    explanation_links
  } = configuration;
  const [contextExpanded, setContextExpanded] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [interventionCosts, setInterventionCosts] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [interventionShortDescriptions, setInterventionShortDescriptions] = useState(t2);
  const [selectedIntervention, setSelectedIntervention] = useState(interventions ? interventions[0].title : "");
  const [, setWidgetContext] = useContext(WidgetContext);
  const plausible = usePlausible();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Date();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const currentDate = t3;
  let t4;
  let t5;
  if ($[3] !== configuration.locale || $[4] !== currency || $[5] !== interventions) {
    t4 = () => {
      if (interventions && interventions.length > 0) {
        const url = `https://impact.gieffektivt.no/api/evaluations?${interventions.map(_temp).join("")}currency=${stegaClean(currency)}&language=${stegaClean(configuration.locale)}&conversion_year=${currentDate.getFullYear()}&conversion_month=${currentDate.getMonth() + 1}`;
        fetch(url).then(res => {
          res.json().then(data => {
            const costs = new Map();
            const shortDescriptions = new Map();
            const evaluations = data.evaluations;
            interventions.forEach(i_0 => {
              const filtered = evaluations.filter(e => e.charity.abbreviation === stegaClean(i_0.abbreviation));
              const ordered = filtered.sort(_temp2);
              const evaluation = ordered[0];
              costs.set(stegaClean(i_0.abbreviation), evaluation.converted_cost_per_output);
              shortDescriptions.set(stegaClean(i_0.abbreviation), evaluation.intervention.short_description);
            });
            setInterventionCosts(costs);
            setInterventionShortDescriptions(shortDescriptions);
          });
        }).catch(_temp3);
      }
    };
    t5 = [interventions, currency, configuration.locale, currentDate];
    $[3] = configuration.locale;
    $[4] = currency;
    $[5] = interventions;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  if (typeof interventions === "undefined" || interventions.length === 0) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <span>No internventions</span>;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let t10;
  let t11;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== configuration.donate_button || $[10] !== configuration.donate_label_short || $[11] !== contextExpanded || $[12] !== explanation_label || $[13] !== explanation_links || $[14] !== explanation_text || $[15] !== interventionCosts || $[16] !== interventionShortDescriptions || $[17] !== interventions || $[18] !== plausible || $[19] !== selectedIntervention || $[20] !== setWidgetContext || $[21] !== sum) {
    t11 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t12;
      if ($[28] !== interventionCosts || $[29] !== interventionShortDescriptions) {
        t12 = i_1 => ({
          title: i_1.title,
          abbreviation: i_1.abbreviation,
          pricePerOutput: interventionCosts.get(stegaClean(i_1.abbreviation)),
          outputStringTemplate: i_1.template_string,
          organizationName: i_1.organization_name,
          organizationId: i_1.organization_id,
          causeAreaId: i_1.cause_area_id,
          shortDescription: interventionShortDescriptions.get(stegaClean(i_1.abbreviation)) || ""
        });
        $[28] = interventionCosts;
        $[29] = interventionShortDescriptions;
        $[30] = t12;
      } else {
        t12 = $[30];
      }
      const mappedInterventions = interventions.map(t12);
      let t13;
      if ($[31] !== selectedIntervention) {
        t13 = i_2 => stegaClean(i_2.title) === selectedIntervention;
        $[31] = selectedIntervention;
        $[32] = t13;
      } else {
        t13 = $[32];
      }
      const currentIntervention = mappedInterventions.find(t13) as Intervention;
      if (!currentIntervention) {
        setSelectedIntervention(stegaClean(mappedInterventions[0].title));
        let t14;
        if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = <Spinner />;
          $[33] = t14;
        } else {
          t14 = $[33];
        }
        t11 = t14;
        break bb0;
      }
      const output = currentIntervention.pricePerOutput ? Math.round(sum / currentIntervention.pricePerOutput) : 0;
      const loading = typeof currentIntervention.pricePerOutput === "undefined";
      let outputString;
      if (isNaN(output)) {
        outputString = "0";
      } else {
        outputString = output.toString();
      }
      t9 = styles.wrapper;
      let t14;
      if ($[34] !== interventions || $[35] !== selectedIntervention) {
        let t15;
        if ($[37] !== selectedIntervention) {
          t15 = i_3 => <button data-cy={`${i_3.title}-button`} key={i_3.title} onClick={e_0 => {
            e_0.currentTarget.blur();
            setSelectedIntervention(stegaClean(i_3.title));
          }} className={stegaClean(i_3.title) === selectedIntervention ? styles.selected : ""}>{i_3.title}</button>;
          $[37] = selectedIntervention;
          $[38] = t15;
        } else {
          t15 = $[38];
        }
        t14 = interventions.map(t15);
        $[34] = interventions;
        $[35] = selectedIntervention;
        $[36] = t14;
      } else {
        t14 = $[36];
      }
      if ($[39] !== t14) {
        t10 = <div className={styles.select}>{t14}</div>;
        $[39] = t14;
        $[40] = t10;
      } else {
        t10 = $[40];
      }
      t6 = styles.output;
      if ($[41] !== loading) {
        t7 = loading && <div className={styles.spinnerWrapper}><Spinner /></div>;
        $[41] = loading;
        $[42] = t7;
      } else {
        t7 = $[42];
      }
      t8 = !loading && <><span className="detailheader">{currentIntervention.organizationName}</span><div className={styles.paragraphWrapper}><span data-cy="impact-output" className={styles.paragraphNumber}>{thousandize(parseInt(outputString))}</span><div className={styles.explanatory}><p><span className={styles.innerParagraphNumber}>{outputString}</span>{currentIntervention.outputStringTemplate}</p><span className={contextExpanded ? styles.captionopen : ""} onClick={() => setContextExpanded(!contextExpanded)}>{explanation_label}  </span></div></div><AnimateHeight duration={300} height={contextExpanded ? "auto" : 0} animateOpacity={true}><div className={styles.context}><PortableText value={explanation_text} /></div>{explanation_links && <Links links={explanation_links} />}</AnimateHeight>{configuration.donate_button && <div className={styles.giveButtonWrapper}><EffektButton onClick={() => {
            plausible("OpenDonationWidget", {
              props: {
                page: window.location.pathname
              }
            });
            plausible("OpenDonationWidgetInterventionWidgetCTA", {
              props: {
                page: window.location.pathname
              }
            });
            setWidgetContext({
              open: true,
              prefilled: [{
                causeAreaId: currentIntervention.causeAreaId,
                share: 100,
                organizations: [{
                  organizationId: currentIntervention.organizationId,
                  share: 100
                }]
              }],
              prefilledSum: sum
            });
          }}>{configuration.donate_label_short?.replace(".", "") || "Gi"}{" "}{thousandize(parseInt(outputString))}{" "}{currentIntervention.shortDescription.toLowerCase()}</EffektButton></div>}</>;
    }
    $[9] = configuration.donate_button;
    $[10] = configuration.donate_label_short;
    $[11] = contextExpanded;
    $[12] = explanation_label;
    $[13] = explanation_links;
    $[14] = explanation_text;
    $[15] = interventionCosts;
    $[16] = interventionShortDescriptions;
    $[17] = interventions;
    $[18] = plausible;
    $[19] = selectedIntervention;
    $[20] = setWidgetContext;
    $[21] = sum;
    $[22] = t10;
    $[23] = t11;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t10 = $[22];
    t11 = $[23];
    t6 = $[24];
    t7 = $[25];
    t8 = $[26];
    t9 = $[27];
  }
  if (t11 !== Symbol.for("react.early_return_sentinel")) {
    return t11;
  }
  let t12;
  if ($[43] !== t6 || $[44] !== t7 || $[45] !== t8) {
    t12 = <div className={t6}>{t7}{t8}</div>;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== t10 || $[48] !== t12 || $[49] !== t9) {
    t13 = <div className={t9}>{t10}{t12}</div>;
    $[47] = t10;
    $[48] = t12;
    $[49] = t9;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};
function _temp(i) {
  return `charity_abbreviation=${stegaClean(i.abbreviation)}&`;
}
function _temp2(a, b) {
  return b.start_year - a.start_year;
}
function _temp3(err) {
  console.error(err);
}